import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCalls } from "@/lib/calls";
import { useSession } from "@/lib/session";
import { useSoftphone } from "@/lib/softphone";
import { defaultSipWsForHost, resolveSipWsForBrowser } from "@/lib/sipWs";
import { localNumberInput, normalizeLocalNumber } from "@/lib/shared";

const KEYS: Array<{ digit: string; letters?: string }> = [
  { digit: "1" },
  { digit: "2", letters: "ABC" },
  { digit: "3", letters: "DEF" },
  { digit: "4", letters: "GHI" },
  { digit: "5", letters: "JKL" },
  { digit: "6", letters: "MNO" },
  { digit: "7", letters: "PQRS" },
  { digit: "8", letters: "TUV" },
  { digit: "9", letters: "WXYZ" },
  { digit: "*" },
  { digit: "0", letters: "+" },
  { digit: "#" },
];

function formatDial(value: string) {
  const d = value.replace(/\D/g, "").slice(0, 12);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)} ${d.slice(5)}`;
}

function fmtTimer(sec: number | string) {
  const n = Math.max(0, Number(sec) || 0);
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

type SoftSettings = { sipHost: string; sipWsUrl: string };

export default function SoftphonePage() {
  const { user, refresh } = useSession();
  const { hangup: deskHangup, myCall, freeLine, setCallFrom } = useCalls();
  const phone = useSoftphone();
  const [number, setNumber] = useState("");
  const [sipPassword, setSipPassword] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState<SoftSettings>({ sipHost: "", sipWsUrl: "" });
  const [editHost, setEditHost] = useState("");
  const [editWs, setEditWs] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const [showAdmin, setShowAdmin] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const inSipCall = ["inviting", "ringing", "active"].includes(phone.callState);
  const inDeskCall = Boolean(myCall);
  const registered = phone.sipState === "registered";
  const connecting =
    phone.sipState === "registering" || phone.wsState === "connecting";

  useEffect(() => {
    const pending = sessionStorage.getItem("ipbs_pending_dial");
    if (pending) {
      setNumber(localNumberInput(pending));
      sessionStorage.removeItem("ipbs_pending_dial");
    }
  }, []);

  useEffect(() => {
    void api<SoftSettings>("/softphone/settings")
      .then((data) => {
        const host = data.sipHost || "192.168.0.180";
        // On LAN http app, force direct PBX WebSocket (ignore broken cloud wss)
        const ws = resolveSipWsForBrowser(host, data.sipWsUrl || "");
        setSettings({ sipHost: host, sipWsUrl: ws });
        setEditHost(host);
        setEditWs(ws);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (phone.callState === "active" && !callStartedAt) setCallStartedAt(Date.now());
    if (phone.callState === "idle" || phone.callState === "ended") setCallStartedAt(null);
  }, [phone.callState, callStartedAt]);

  useEffect(() => {
    if (!inSipCall) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [inSipCall]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) {
        return;
      }
      if (e.key >= "0" && e.key <= "9") {
        if (inSipCall) phone.sendDtmf(e.key);
        else setNumber((p) => localNumberInput(p + e.key));
      }
      if (e.key === "Backspace" && !inSipCall) {
        e.preventDefault();
        setNumber((p) => p.slice(0, -1));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inSipCall, phone]);

  async function saveSipSettings(e: FormEvent) {
    e.preventDefault();
    setSavingSettings(true);
    setError("");
    try {
      const host = editHost.trim() || "192.168.0.180";
      const ws = resolveSipWsForBrowser(
        host,
        editWs.trim() || defaultSipWsForHost(host),
      );
      const data = await api<SoftSettings>("/softphone/settings", {
        method: "PUT",
        body: JSON.stringify({ sipHost: host, sipWsUrl: ws }),
      });
      setSettings({
        sipHost: data.sipHost,
        sipWsUrl: resolveSipWsForBrowser(data.sipHost, data.sipWsUrl),
      });
      setEditWs(resolveSipWsForBrowser(data.sipHost, data.sipWsUrl));
      setOk("SIP settings saved — tap Connect");
      setShowAdmin(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save SIP settings");
    } finally {
      setSavingSettings(false);
    }
  }

  /** One-click: save mode + connect SIP (mic is optional for register). */
  async function connectSoftphone() {
    setBusy(true);
    setError("");
    setOk("");
    try {
      if (!user?.extension) {
        setError("Map extension 1002 (or your ext) to this user in Users first.");
        return;
      }
      if (!settings.sipHost || !settings.sipWsUrl) {
        setError("Admin must set SIP host + WebSocket first (Open PBX settings below).");
        setShowAdmin(true);
        return;
      }
      await api("/softphone/me", {
        method: "PATCH",
        body: JSON.stringify({
          phoneMode: "sip",
          ...(sipPassword ? { sipPassword } : {}),
        }),
      });
      await setCallFrom("sip").catch(() => {});
      await refresh();
      // Do not enableMic here — it was showing "Requested device not found" and confusing connect
      const okReg = await phone.connect(sipPassword);
      if (okReg) setOk(`Connected — extension ${user.extension} registered`);
      else setError(phone.error || "Could not connect — check SIP password / WebSocket");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Connect failed");
    } finally {
      setBusy(false);
    }
  }

  async function dial() {
    const ten = localNumberInput(number);
    if (ten.length < 8) {
      setError("Enter a phone number (at least 8 digits).");
      return;
    }
    const dialNumber = ten.length === 10 ? normalizeLocalNumber(ten) : ten;
    setError("");
    setOk("");
    setBusy(true);
    try {
      if (!registered) {
        const okReg = await phone.connect(sipPassword);
        if (!okReg) throw new Error(phone.error || "Softphone not registered");
      }
      const okMic = phone.micOk || (await phone.enableMic());
      if (!okMic) throw new Error("Allow microphone before calling");
      await phone.call(dialNumber);
      setOk(`Calling ${dialNumber}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Call failed");
    } finally {
      setBusy(false);
    }
  }

  function pad(n: string) {
    if (!/^\d$/.test(n)) return;
    if (inSipCall) {
      phone.sendDtmf(n);
      return;
    }
    setNumber((p) => localNumberInput(p + n));
  }

  const liveSeconds =
    callStartedAt != null ? Math.floor((Date.now() - callStartedAt) / 1000) : 0;
  void tick;

  const statusLabel = registered
    ? "Online"
    : connecting || busy
      ? "Connecting…"
      : phone.sipState === "failed"
        ? "Failed"
        : "Offline";

  return (
    <>
      <div className="page-toolbar">
        <div>
          <p className="eyebrow">Telephony</p>
          <h1 className="page-title">Softphone</h1>
          <p className="muted">
            Enter SIP password → Connect. Talk with headset mic — no desk phone.
          </p>
        </div>
        {(inSipCall || inDeskCall) && (
          <button
            className="btn danger"
            type="button"
            onClick={() => {
              if (inSipCall) phone.hangup();
              else if (myCall) void deskHangup(myCall.uuid);
              else void freeLine();
            }}
          >
            Hang up
          </button>
        )}
      </div>

      <div className="softphone-page softphone-page--wide">
        <section className="panel softphone-status">
          <div className="softphone-status-row">
            <span className="muted">Status</span>
            <span
              className={`pill ${
                registered ? "live" : phone.sipState === "failed" ? "dead" : "idle"
              }`}
            >
              {statusLabel}
            </span>
          </div>
          <div className="softphone-status-row">
            <span className="muted">Extension</span>
            <b>{user?.extension || phone.extension || "—"}</b>
            {!user?.extension ? (
              <Link className="linkish" to="/app/users">
                Map in Users
              </Link>
            ) : null}
          </div>
          <div className="softphone-status-row">
            <span className="muted">PBX</span>
            <code style={{ fontSize: 12 }}>
              {settings.sipHost || "not set"} · {settings.sipWsUrl || "no WS"}
            </code>
          </div>
          <div className="softphone-status-row">
            <span className="muted">Neron LuCI</span>
            <span
              className={`pill ${
                phone.pbxStatus && !/unavail|offline|logout/i.test(phone.pbxStatus)
                  ? "live"
                  : phone.pbxStatus
                    ? "dead"
                    : "idle"
              }`}
            >
              {phone.pbxStatus || (registered ? "syncing…" : "—")}
            </span>
            {registered ? (
              <button
                className="btn ghost btn-tiny"
                type="button"
                onClick={() => void phone.refreshPbxStatus()}
              >
                Refresh status
              </button>
            ) : null}
          </div>
          <div className="softphone-status-row">
            <span className="muted">Microphone</span>
            <span className={`pill ${phone.micOk ? "live" : "idle"}`}>
              {phone.micOk ? "Ready" : "Allow when calling"}
            </span>
            <button className="btn ghost btn-tiny" type="button" onClick={() => void phone.enableMic()}>
              Allow mic
            </button>
          </div>

          <div className="softphone-auth">
            <label className="field">
              <span>SIP password (same as extension on Neron)</span>
              <input
                type="password"
                value={sipPassword}
                onChange={(e) => setSipPassword(e.target.value)}
                placeholder={user?.sipPasswordSet ? "•••••••• (saved — leave blank to reuse)" : "Extension SIP password"}
                autoComplete="current-password"
              />
            </label>
            <div className="btn-row">
              <button
                className="btn"
                type="button"
                disabled={busy || connecting}
                onClick={() => void connectSoftphone()}
              >
                {busy || connecting
                  ? "Connecting…"
                  : registered
                    ? "Reconnect"
                    : "Connect"}
              </button>
              {registered ? (
                <button className="btn ghost" type="button" onClick={() => phone.disconnect()}>
                  Disconnect
                </button>
              ) : null}
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              Connect registers your extension on the PBX. Mic is only required when you place a call.
            </p>
          </div>

          {isAdmin ? (
            <div style={{ marginTop: 8 }}>
              <button
                className="btn ghost btn-tiny"
                type="button"
                onClick={() => setShowAdmin((v) => !v)}
              >
                {showAdmin ? "Hide PBX settings" : "PBX settings (admin)"}
              </button>
              {showAdmin ? (
                <form className="softphone-auth" onSubmit={(e) => void saveSipSettings(e)}>
                  <label className="field">
                    <span>SIP host (PBX IP)</span>
                    <input
                      value={editHost}
                      onChange={(e) => {
                        const h = e.target.value;
                        setEditHost(h);
                        if (!editWs || editWs.includes(editHost) || /\/w$/i.test(editWs)) {
                          setEditWs(defaultSipWsForHost(h.trim()) || editWs);
                        }
                      }}
                      placeholder="192.168.0.180"
                    />
                  </label>
                  <label className="field">
                    <span>SIP WebSocket</span>
                    <input
                      value={editWs}
                      onChange={(e) => setEditWs(e.target.value)}
                      placeholder="ws://192.168.0.180:8088/ws"
                    />
                  </label>
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    On LAN (this PC): use <code>ws://192.168.0.180:8088/ws</code>
                    <br />
                    Do <b>not</b> use cloud <code>wss://ipbx…</code> from the office LAN.
                  </p>
                  <button className="btn ghost" disabled={savingSettings}>
                    {savingSettings ? "Saving…" : "Save PBX settings"}
                  </button>
                </form>
              ) : null}
            </div>
          ) : null}

          {phone.micNotice ? <p className="error">{phone.micNotice}</p> : null}
          {phone.error ? <p className="error">{phone.error}</p> : null}
          {error ? <p className="error">{error}</p> : null}
          {ok ? <p className="ok-msg">{ok}</p> : null}
        </section>

        <section className="panel dialer-card softphone-dialer">
          <div className="panel-head">
            <h3>Dial pad</h3>
            <span className={`pill ${inSipCall ? "live" : registered ? "idle" : "dead"}`}>
              {inSipCall ? phone.callState : registered ? "Ready" : "Offline"}
            </span>
          </div>

          {phone.incoming ? (
            <div className="softphone-live">
              <p className="softphone-live-label">Incoming</p>
              <h2 className="softphone-live-number">{phone.incoming.from}</h2>
              <div className="dial-actions">
                <button className="btn mint" type="button" onClick={() => void phone.answer()}>
                  Answer
                </button>
                <button className="btn danger" type="button" onClick={() => phone.hangup()}>
                  Reject
                </button>
              </div>
            </div>
          ) : inSipCall ? (
            <div className="softphone-live">
              <p className="softphone-live-label">{phone.callState}</p>
              <h2 className="softphone-live-number">{phone.remoteNumber || number}</h2>
              <p className="softphone-live-timer">{fmtTimer(liveSeconds)}</p>
              <div className="dial-actions">
                <button
                  className="btn ghost"
                  type="button"
                  onClick={() => phone.mute(!phone.muted)}
                >
                  {phone.muted ? "Unmute" : "Mute"}
                </button>
                <button className="btn danger" type="button" onClick={() => phone.hangup()}>
                  Hang up
                </button>
              </div>
            </div>
          ) : (
            <div className="dial-display">
              <input
                aria-label="Number to dial"
                placeholder="Number"
                value={formatDial(number)}
                inputMode="numeric"
                onChange={(e) => setNumber(localNumberInput(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && number && !busy) {
                    e.preventDefault();
                    void dial();
                  }
                }}
              />
              <button
                className="dial-back"
                type="button"
                onClick={() => setNumber((p) => p.slice(0, -1))}
                disabled={!number}
                aria-label="Backspace"
              >
                ⌫
              </button>
            </div>
          )}

          {!phone.incoming ? (
            <div className="dial-pad">
              {KEYS.map((k) => (
                <button
                  key={k.digit}
                  type="button"
                  className={k.digit === "0" ? "dial-zero" : undefined}
                  onClick={() => pad(k.digit)}
                  disabled={k.digit === "*" || k.digit === "#"}
                >
                  <b>{k.digit}</b>
                  {k.letters ? <span>{k.letters}</span> : <span>&nbsp;</span>}
                </button>
              ))}
            </div>
          ) : null}

          {!inSipCall && !phone.incoming ? (
            <>
              <p className="muted dial-hint">
                {localNumberInput(number).length === 10 ? (
                  <>
                    Will dial <b>0{localNumberInput(number)}</b> via softphone
                  </>
                ) : (
                  "Connect first, then dial"
                )}
              </p>
              <div className="dial-actions">
                <button
                  className="btn mint"
                  type="button"
                  disabled={busy || localNumberInput(number).length < 8}
                  onClick={() => void dial()}
                >
                  {busy ? "Connecting…" : "Call"}
                </button>
                <button className="btn ghost" type="button" onClick={() => setNumber("")}>
                  Clear
                </button>
              </div>
            </>
          ) : null}

          {phone.log.length ? (
            <div className="sip-log" style={{ marginTop: 12 }}>
              {phone.log.slice(-8).map((row, i) => (
                <p key={`${row.at}-${i}`} className={`sip-log-${row.level}`}>
                  <span>{row.at}</span>
                  {row.msg}
                </p>
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
}
